/*
 * Creative Commons attribution in TASL form: Title, Author, Source, License.
 *
 * Sources, read in October 2026:
 * - Creative Commons, "Best practices for attribution" (wiki.creativecommons.org): TASL
 *   (title, author, source, licence); attribution must be reasonable for the medium; the
 *   licence is named and linked ("CC BY 4.0", never just "Creative Commons"); the title is
 *   required by licence versions 3.0 and earlier and optional in 4.0; the author is the
 *   name the licensor asks for, and a hosting site is not a substitute; a modification is
 *   noted ("used under CC BY 4.0 / Cropped from original"); an adaptation names the original
 *   ("This work, "90fied", is adapted from ... used under CC BY 4.0. "90fied" is licensed
 *   under CC BY 4.0 by [name]"); a printed copy spells the URLs out; a work in the public
 *   domain needs no credit by law, but the institution that holds it is worth naming.
 * - The legal code of CC BY 4.0, section 3(a)(1): keep the creator's name, a copyright
 *   notice, a reference to the licence and the disclaimer, and a link to the work if
 *   supplied; "indicate if You modified the Licensed Material"; and the licence's URI. Of
 *   CC BY 3.0, section 4(b): the author's name, "the title of the Work if supplied", the
 *   URI the licensor specifies, and for an adaptation "a credit identifying the use of the
 *   Work".
 * - Licence addresses: creativecommons.org/licenses/<code>/<version>/ for the six licences
 *   in versions 1.0 to 4.0 (the 1.0 NoDerivs NonCommercial licence lives at by-nd-nc/1.0),
 *   creativecommons.org/publicdomain/zero/1.0/ for CC0 and
 *   creativecommons.org/publicdomain/mark/1.0/ for the Public Domain Mark. Each address
 *   was fetched and exists. The 4.0 licences are the ones Creative Commons recommends; the
 *   older versions are kept for works already licensed under them.
 *
 * The page examples are checked against these functions in
 * `src/lib/__tests__/legal-styles-creative-commons.test.ts`.
 */

export type LicenceCode =
  | "by"
  | "by-sa"
  | "by-nc"
  | "by-nc-sa"
  | "by-nd"
  | "by-nc-nd"
  | "cc0"
  | "pdm";

export const LICENCE_CODES: readonly LicenceCode[] = [
  "by",
  "by-sa",
  "by-nc",
  "by-nc-sa",
  "by-nd",
  "by-nc-nd",
  "cc0",
  "pdm",
];

export type Version = "4.0" | "3.0" | "2.5" | "2.0" | "1.0";

export const VERSIONS: readonly Version[] = ["4.0", "3.0", "2.5", "2.0", "1.0"];

const SPOKEN: Record<LicenceCode, string> = {
  by: "BY",
  "by-sa": "BY-SA",
  "by-nc": "BY-NC",
  "by-nc-sa": "BY-NC-SA",
  "by-nd": "BY-ND",
  "by-nc-nd": "BY-NC-ND",
  cc0: "CC0",
  pdm: "PDM",
};

export function isPublicDomain(code: LicenceCode): boolean {
  return code === "cc0" || code === "pdm";
}

export function hasNoDerivatives(code: LicenceCode): boolean {
  return code === "by-nd" || code === "by-nc-nd";
}

export function hasShareAlike(code: LicenceCode): boolean {
  return code === "by-sa" || code === "by-nc-sa";
}

export function hasNonCommercial(code: LicenceCode): boolean {
  return code === "by-nc" || code === "by-nc-sa" || code === "by-nc-nd";
}

/** The URL slug: the 1.0 NoDerivs NonCommercial licence is "by-nd-nc". */
function slug(code: LicenceCode, version: Version): string {
  return code === "by-nc-nd" && version === "1.0" ? "by-nd-nc" : code;
}

/** "CC BY 4.0", "CC BY-ND-NC 1.0", "CC0 1.0". The Public Domain Mark has its own name. */
export function licenceName(code: LicenceCode, version: Version): string {
  if (code === "cc0") {
    return "CC0 1.0";
  }
  if (code === "pdm") {
    return "Public Domain Mark 1.0";
  }
  const spoken =
    code === "by-nc-nd" && version === "1.0" ? "BY-ND-NC" : SPOKEN[code];
  return `CC ${spoken} ${version}`;
}

/** The deed: the page that summarises the licence and the address attribution links to. */
export function licenceUrl(code: LicenceCode, version: Version): string {
  if (code === "cc0") {
    return "https://creativecommons.org/publicdomain/zero/1.0/";
  }
  if (code === "pdm") {
    return "https://creativecommons.org/publicdomain/mark/1.0/";
  }
  return `https://creativecommons.org/licenses/${slug(code, version)}/${version}/`;
}

/** The full licence text. The Public Domain Mark is a label, not a licence, and has none. */
export function legalCodeUrl(code: LicenceCode, version: Version): string {
  return code === "pdm" ? "" : `${licenceUrl(code, version)}legalcode`;
}

/** The versions that exist for a licence: CC0 and the Public Domain Mark have only 1.0. */
export function versionsFor(code: LicenceCode): readonly Version[] {
  return isPublicDomain(code) ? ["1.0"] : VERSIONS;
}

/* ---------------------------------------------------------------- the credit */

/** A run of text, optionally a link. */
export interface Segment {
  text: string;
  href?: string;
  /** The licence link, which carries rel="license" in HTML. */
  licence?: boolean;
}

export interface AttributionInput {
  title: string;
  sourceUrl: string;
  author: string;
  authorUrl: string;
  /** "© 2019–2022 Kyle Simpson" — kept as supplied. */
  notice: string;
  /** The museum, library or archive that holds a public-domain work. */
  institution: string;
  licence: LicenceCode;
  version: Version;
  anonymous: boolean;
  modified: boolean;
  /** "Cropped from original" */
  change: string;
  /** The title of an adaptation you made, and your own name. */
  adaptationTitle: string;
  adapter: string;
}

/** The wording around the elements; the widget supplies it in the reader's language. */
export interface Phrases {
  /** {work} by {author} is licensed under {licence}. */
  licensed: string;
  /** {work} is licensed under {licence}. */
  licensedNoAuthor: string;
  /** {work} by {author}, used under {licence} / {change} */
  modified: string;
  /** {work}, used under {licence} / {change} */
  modifiedNoAuthor: string;
  /** This work, {new}, is adapted from {work} by {author}, used under {licence}. {new} is licensed under {licence2} by {adapter}. */
  adaptation: string;
  adaptationNoAuthor: string;
  /** {work} by {author}. {institution} {status} */
  publicDomain: string;
  publicDomainNoAuthor: string;
  /** Dedicated to the public domain under {licence}. */
  dedicated: string;
  /** Marked as being in the public domain ({licence}). */
  marked: string;
  /** {work} by {author} / {licence} */
  caption: string;
  captionNoAuthor: string;
  /** The title when there is none: "This work". */
  untitled: string;
  /** Opening and closing quotation marks around a title. */
  quoteOpen: string;
  quoteClose: string;
  /** "{text} ({url})" for the printed form. */
  printUrl: string;
}

export interface Attribution {
  /** The credit for a web page: linked elements. */
  web: Segment[];
  /** The same credit with the URLs spelled out, for print and PDF. */
  print: string;
  /** Plain text with no links. */
  plain: string;
  /** HTML with rel="license" on the licence link. */
  html: string;
  /** A short credit for a caption or a slide. */
  caption: Segment[];
  /** The licence's name and addresses. */
  licenceName: string;
  deedUrl: string;
  legalCodeUrl: string;
  notes: AttributionNote[];
}

export type AttributionNote =
  | "noDerivatives"
  | "shareAlike"
  | "nonCommercial"
  | "titleRequired"
  | "olderVersion"
  | "publicDomain"
  | "anonymous"
  | "removeOnRequest";

const RE_TOKEN = /\{(\w+)\}/g;

/** Fill a template's {tokens} with runs of segments, keeping its own text as plain text. */
export function fillTemplate(
  template: string,
  tokens: Record<string, Segment[]>
): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  for (const match of template.matchAll(RE_TOKEN)) {
    const start = match.index ?? 0;
    if (start > last) {
      out.push({ text: template.slice(last, start) });
    }
    out.push(...(tokens[match[1]] ?? [{ text: match[0] }]));
    last = start + match[0].length;
  }
  if (last < template.length) {
    out.push({ text: template.slice(last) });
  }
  return out;
}

function workSegments(input: AttributionInput, phrases: Phrases): Segment[] {
  const href = input.sourceUrl || undefined;
  if (input.title === "") {
    return [{ text: phrases.untitled, href }];
  }
  return [
    { text: `${phrases.quoteOpen}${input.title}${phrases.quoteClose}`, href },
  ];
}

function authorSegments(input: AttributionInput): Segment[] {
  return [{ text: input.author, href: input.authorUrl || undefined }];
}

function licenceSegments(code: LicenceCode, version: Version): Segment[] {
  return [
    {
      text: licenceName(code, version),
      href: licenceUrl(code, version),
      licence: true,
    },
  ];
}

const RE_ENDS_WITH_STOP = /[.!?]$/;

function withNotice(segments: Segment[], notice: string): Segment[] {
  if (notice === "") {
    return segments;
  }
  const stop = RE_ENDS_WITH_STOP.test(notice) ? "" : ".";
  return [...segments, { text: ` ${notice}${stop}` }];
}

function hasAuthor(input: AttributionInput): boolean {
  return input.author !== "" && !input.anonymous;
}

function mainCredit(input: AttributionInput, phrases: Phrases): Segment[] {
  const work = workSegments(input, phrases);
  const author = authorSegments(input);
  const licence = licenceSegments(input.licence, input.version);
  const named = hasAuthor(input);
  if (isPublicDomain(input.licence)) {
    const status = fillTemplate(
      input.licence === "cc0" ? phrases.dedicated : phrases.marked,
      { licence }
    );
    const institution =
      input.institution === "" ? [] : [{ text: `${input.institution}. ` }];
    const lead = fillTemplate(
      named ? phrases.publicDomain : phrases.publicDomainNoAuthor,
      { work, author }
    );
    return [...lead, ...institution, ...status];
  }
  if (input.modified && input.adaptationTitle !== "") {
    const fresh = [
      { text: phrases.quoteOpen },
      { text: input.adaptationTitle },
      { text: phrases.quoteClose },
    ];
    return fillTemplate(
      named ? phrases.adaptation : phrases.adaptationNoAuthor,
      {
        new: fresh,
        work,
        author,
        licence,
        licence2: licence,
        adapter: [{ text: input.adapter || "…" }],
      }
    );
  }
  if (input.modified && input.change !== "") {
    return fillTemplate(named ? phrases.modified : phrases.modifiedNoAuthor, {
      work,
      author,
      licence,
      change: [{ text: input.change }],
    });
  }
  return fillTemplate(named ? phrases.licensed : phrases.licensedNoAuthor, {
    work,
    author,
    licence,
  });
}

function segmentsToPlain(segments: Segment[]): string {
  return segments.map((segment) => segment.text).join("");
}

function segmentsToPrint(segments: Segment[], phrases: Phrases): string {
  return segments
    .map((segment) =>
      segment.href
        ? phrases.printUrl
            .replace("{text}", segment.text)
            .replace("{url}", segment.href)
        : segment.text
    )
    .join("");
}

const RE_AMPERSAND = /&/g;
const RE_LESS = /</g;
const RE_GREATER = />/g;
const RE_QUOTE = /"/g;

/** Text needs only &, < and > escaped; an attribute value needs the double quote too. */
function escapeText(text: string): string {
  return text
    .replace(RE_AMPERSAND, "&amp;")
    .replace(RE_LESS, "&lt;")
    .replace(RE_GREATER, "&gt;");
}

function escapeAttribute(text: string): string {
  return escapeText(text).replace(RE_QUOTE, "&quot;");
}

function segmentsToHtml(segments: Segment[]): string {
  return segments
    .map((segment) => {
      const text = escapeText(segment.text);
      if (!segment.href) {
        return text;
      }
      const rel = segment.licence ? ' rel="license"' : "";
      return `<a href="${escapeAttribute(segment.href)}"${rel}>${text}</a>`;
    })
    .join("");
}

function captionCredit(input: AttributionInput, phrases: Phrases): Segment[] {
  return fillTemplate(
    hasAuthor(input) ? phrases.caption : phrases.captionNoAuthor,
    {
      work: workSegments(input, phrases),
      author: authorSegments(input),
      licence: licenceSegments(input.licence, input.version),
    }
  );
}

function notesFor(input: AttributionInput): AttributionNote[] {
  const notes: AttributionNote[] = [];
  const { licence, version } = input;
  if (isPublicDomain(licence)) {
    notes.push("publicDomain");
    return notes;
  }
  if (hasNoDerivatives(licence) && input.modified) {
    notes.push("noDerivatives");
  }
  if (hasShareAlike(licence) && input.modified) {
    notes.push("shareAlike");
  }
  if (hasNonCommercial(licence)) {
    notes.push("nonCommercial");
  }
  if (version !== "4.0" && input.title === "") {
    notes.push("titleRequired");
  }
  if (version !== "4.0") {
    notes.push("olderVersion");
  } else {
    notes.push("removeOnRequest");
  }
  if (input.anonymous) {
    notes.push("anonymous");
  }
  return notes;
}

export function isReady(input: AttributionInput): boolean {
  if (isPublicDomain(input.licence)) {
    return input.title !== "" || input.sourceUrl !== "";
  }
  return input.title !== "" || input.author !== "" || input.sourceUrl !== "";
}

/** Everything the generator shows for one set of details. */
export function buildAttribution(
  input: AttributionInput,
  phrases: Phrases
): Attribution {
  const web = withNotice(mainCredit(input, phrases), input.notice);
  return {
    web,
    print: segmentsToPrint(web, phrases),
    plain: segmentsToPlain(web),
    html: segmentsToHtml(web),
    caption: captionCredit(input, phrases),
    licenceName: licenceName(input.licence, input.version),
    deedUrl: licenceUrl(input.licence, input.version),
    legalCodeUrl: legalCodeUrl(input.licence, input.version),
    notes: notesFor(input),
  };
}

export { segmentsToHtml, segmentsToPlain, segmentsToPrint };
